// package imports
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useSession } from 'next-auth/react';
import { Backdrop } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Link from 'next/link';

// local imports
import {
  logClientException,
  logServerException,
} from 'appinsights/clientAppInsights';
import {
  BehaviorBannerContainer,
  Breadcrumb,
  FullWidthHR,
  SubHeadingContainer,
} from '../LessonPlanDetailComponents';
import PleaseWait from '../../common/PleaseWait';
import NewsLetterComponent from '../NewsletterComponent';
import BreadcrumbTrail from '../BreadcrumbTrail';
import { isCustomer } from 'services/subscriptionUtils';
import BackToTopButton from 'components/common/BackToTopButton';
import PrimaryAction from './PrimaryAction';
import AdditionalSection from './AdditionalSection';

export const DashedHr = styled('hr', {
  shouldForwardProp: (prop) => prop !== 'topMargin',
})(({ theme, topMargin }) => ({
  width: '100`%',
  marginTop: topMargin,
  borderTop: `.5px dashed ${theme.palette.grey.A400}`,
}));

const BehaviorChallengesDetails = (props) => {
  const {
    customData,
    module: { fields },
  } = props;
  const detailPage = customData.detailPage;
  const theme = useTheme();
  const { data: session, status } = useSession();
  const [loading, setLoading] = useState(false);

  const queryParams =
    typeof window === 'undefined' ? null : new URLSearchParams(location.search);

  const skills = queryParams?.get('skills')?.split(',') || [];
  const _grades = queryParams?.get('grades')?.split(',') || [];

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        //await logResourceAccess('Lesson Plan', lessonPlan?.fields?.title, lessonPlan.fields.slug, 'lesson_plan_detail_access');
        setLoading(false);
      } catch (error) {
        setLoading(false);
        logClientException(error);
      }
    };

    if (status === 'authenticated' && isCustomer(session?.user)) {
      init();
    } else if (
      (status === 'unauthenticated' ||
        (status === 'authenticated' && !isCustomer(session?.user))) &&
      typeof window !== 'undefined'
    ) {
      window.onscroll = () => {
        let y = window.scrollY;
        if (y > 500) {
          window.scrollTo(0, 500);
        }
      };
      return () => {
        window.onscroll = null;
      };
    }
  }, [session?.user, status]);

  const breakCrumbArgs = () => {
    const args = {
      fields: {
        borderColor: 'A400',
        hideComponent: 'never',
        textColor: 'medium',
        breadcrumbList: [],
      },
    };
    args.fields.breadcrumbList.push({
      contentID: 0,
      fields: {
        breadcrumbUrl: { href: '/', text: 'Home' },
        name: 'Home',
        order: 0,
      },
    });
    args.fields.breadcrumbList.push({
      contentID: 1,
      fields: {
        breadcrumbUrl: {
          href: '/premium-resources/common-behavioral-challenges-recommendations',
          text: 'Common Behavioral Challenges & Recommendations',
        },
        name: 'Behavior Interactions',
        order: 1,
      },
    });
    args.fields.breadcrumbList.push({
      contentID: 2,
      fields: {
        breadcrumbUrl: {
          href: '',
          text: 'Common Behavioral Challenges & Recommendations Details',
        },
        name: 'Common Behavioral Challenges & Recommendations Details',
        order: 2,
      },
    });
    return args;
  };

  if (status === 'loading') {
    return <PleaseWait isLoading={true} />;
  }

  return (
    <>
      <Box
        sx={[
          {
            width: '100%',
            mt: '1rem',
            ml: 'auto',
            mr: 'auto',
            maxWidth: {
              sm: '540px',
              md: '768px',
              lg: '1180px',
              xl: '1180px',
            },
          },
          status === 'unauthenticated'
            ? {
                maxWidth: {
                  xs: '540px',
                },
              }
            : {
                maxWidth: {
                  xs: '375px',
                  sm: '540px',
                  md: '768px',
                  lg: '1180px',
                  xl: '1180px',
                },
              },
        ]}
      >
        {(status === 'unauthenticated' ||
          (status === 'authenticated' && !isCustomer(session?.user))) && (
          <Backdrop
            sx={(theme) => ({
              zIndex: theme.zIndex.drawer + 1,
              background:
                'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, .5), rgba(0, 0, 0, 1))',
              marginTop: { xs: '100px', sm: '150px' },
            })}
            open={true}
          >
            <div style={{ width: '100%' }}>
              <NewsLetterComponent
                cTAText1={fields.cTAText1}
                cTAText2={fields.cTAText2}
                unsubscribeText={fields.unsubscribeText}
                cTAlink={fields.cTAlink}
                cTALinkColor={fields.cTALinkColor}
                backgroundColor={fields.backgroundColor}
                accentImage={fields.accentImage}
              />
            </div>
          </Backdrop>
        )}
        <PleaseWait isLoading={loading} />
        <BackToTopButton scrollTop={0} scrollTrigger={750} />
        <BehaviorBannerContainer>
          <Box
            component="div"
            sx={{ alignSelf: 'center', position: 'relative' }}
          >
            <Box component="div" className="skillsAndGrades">
              {skills.map((skill, index) => (
                <Box component="div" key={index} className="skill">
                  <Box component="span">{skill}</Box>
                </Box>
              ))}
            </Box>
            <Box
              className="lessonText"
              sx={{
                borderRadius: '.5rem',
              }}
            >
              <h1 style={{ color: theme.palette.black.main, fontWeight: 400 }}>
                {detailPage?.fields?.title}
              </h1>
            </Box>
          </Box>
        </BehaviorBannerContainer>

        <FullWidthHR />
      </Box>
      <BreadcrumbTrail module={breakCrumbArgs()} topMargin={'0'} />
      <SubHeadingContainer topMargin={'0rem'} bottomMargin={'0rem'}>
        <Breadcrumb>
          <Link
            href="/premium-resources/common-behavioral-challenges-recommendations"
            rel="noopener noreferrer"
            title="back to behavioral challenges"
          >
            <ArrowBackIcon />
            Back to Behavioral Challenges
          </Link>
        </Breadcrumb>
      </SubHeadingContainer>
      {detailPage?.fields?.primaryActions?.items?.map((a, i) => {
        return (
          <div key={i}>
            <PrimaryAction action={a.fields} index={i} />
          </div>
        );
      })}
      {detailPage?.fields?.secondaryActionTitle?.length > 0 && (
        <>
          <DashedHr topMargin={'0rem'} />
          <AdditionalSection
            id="secondary-action"
            title={detailPage?.fields?.secondaryActionTitle}
            actions={detailPage?.fields?.secondaryActions?.items}
          />
        </>
      )}
      {detailPage?.fields?.thirdActionTitle?.length > 0 && (
        <>
          <DashedHr />
          <AdditionalSection
            id="third-action"
            title={detailPage?.fields?.thirdActionTitle}
            actions={detailPage?.fields?.tertiaryActions?.items}
          />
        </>
      )}
      {detailPage?.fields?.fourthActionTitle?.length > 0 && (
        <>
          <DashedHr />
          <AdditionalSection
            id="fourth-action"
            title={detailPage?.fields?.fourthActionTitle}
            actions={detailPage?.fields?.quaternaryActions?.items}
          />
        </>
      )}
      {detailPage?.fields?.fifthActionTitle?.length > 0 && (
        <>
          <DashedHr />
          <AdditionalSection
            id="fifth-action"
            title={detailPage?.fields?.fifthActionTitle}
            actions={detailPage?.fields?.quinaryActions?.items}
          />
        </>
      )}
      {/*<pre>{JSON.stringify(customData, null, 2)}</pre>*/}
      {/*</Box>*/}
    </>
  );
};

// This is called during server-side rendering (not part of NextJS).
BehaviorChallengesDetails.getCustomInitialProps = async ({
  agility,
  languageCode,
  dynamicPageItem,
}) => {
  const api = agility;

  const detailPage = dynamicPageItem;

  try {
    if (detailPage?.fields?.primaryActions?.referencename) {
      let raw = await api.getContentList({
        referenceName: detailPage?.fields?.primaryActions.referencename,
        languageCode,
        take: 10,
        contentLinkDepth: 5,
        expandAllContentLinks: true,
      });

      if (raw) {
        detailPage.fields.primaryActions = raw;
      }
    }

    if (detailPage?.fields?.secondaryActions?.referencename) {
      let raw = await api.getContentList({
        referenceName: detailPage?.fields?.secondaryActions.referencename,
        languageCode,
        take: 10,
        contentLinkDepth: 5,
        expandAllContentLinks: true,
      });

      if (raw) {
        detailPage.fields.secondaryActions = raw;
      }
    }

    if (detailPage?.fields?.tertiaryActions?.referencename) {
      let raw = await api.getContentList({
        referenceName: detailPage?.fields?.tertiaryActions.referencename,
        languageCode,
        take: 10,
        contentLinkDepth: 5,
        expandAllContentLinks: true,
      });

      if (raw) {
        detailPage.fields.tertiaryActions = raw;
      }
    }

    if (detailPage?.fields?.quaternaryActions?.referencename) {
      let raw = await api.getContentList({
        referenceName: detailPage?.fields?.quaternaryActions.referencename,
        languageCode,
        take: 10,
        contentLinkDepth: 5,
        expandAllContentLinks: true,
      });

      if (raw) {
        detailPage.fields.quaternaryActions = raw;
      }
    }

    if (detailPage?.fields?.quinaryActions?.referencename) {
      let raw = await api.getContentList({
        referenceName: detailPage?.fields?.quinaryActions.referencename,
        languageCode,
        take: 10,
        contentLinkDepth: 5,
        expandAllContentLinks: true,
      });

      if (raw) {
        detailPage.fields.quinaryActions = raw;
      }
    }

    return {
      detailPage,
    };
  } catch (error) {
    await logServerException(error);
    if (console) console.error(error);
  }
};

export default BehaviorChallengesDetails;
